import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import XPBar from "./XPBar";
import AvatarPicker from "./AvatarPicker";
import Footer from "./Footer";
import Topbar from "./Topbar";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [selectedAvatarPath, setSelectedAvatarPath] = useState("");
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch profile and teams
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          setError("User not logged in.");
          setLoading(false);
          return;
        }

        // Fetch profile
        const { data: profileRes } = await axios.get(`${apiBase}/api/profile/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        setProfile(profileRes.profile);
        setSelectedBadges(profileRes.profile.display_badges.map((b) => b.badge_name));
        setSelectedAvatarPath(profileRes.profile.profile_image);
        setSelectedTeamId(profileRes.profile.favorite_team_id || null);

        // Fetch teams
        const { data: teamsRes } = await axios.get(`${apiBase}/api/teams`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        setTeams(teamsRes || []);
      } catch (err) {
        console.error("Error fetching profile/teams:", err.message);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const MAX_DISPLAY_BADGES = 5;

  const toggleBadge = (badgeName) => {
    setSelectedBadges((prev) => {
      if (prev.includes(badgeName)) {
        // Unselect badge
        return prev.filter((b) => b !== badgeName);
      } else {
        // Enforce limit
        if (prev.length >= MAX_DISPLAY_BADGES) {
          toast.error(`You can only display up to ${MAX_DISPLAY_BADGES} badges.`);
          return prev;
        }
        return [...prev, badgeName];
      }
    });
  };

  const saveBadges = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        alert("Not logged in");
        return;
      }

      // Prepare badges array for backend
      const badgesToSend = profile.earned_badges
        .filter((b) => selectedBadges.includes(b.badge_name))
        .map((b) => ({
          badge_name: b.badge_name,
          badge_type: b.badge_type,
          badge_image: b.badge_image,
        }));

      await axios.patch(
        `${apiBase}/api/profile/display-badges`,
        { badges: badgesToSend },
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
      toast.success("Badges updated!", {
        style: {
          border: "2px solid gold",
          padding: "12px",
          color: "#fff",
          background: "#111827",
        },
        iconTheme: {
          primary: "#FFD700",
          secondary: "#111827",
        },
      });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to update display badges");
    }
  };

  async function handleSaveAvatar() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) return alert("Not logged in");

      await axios.patch(
        `${apiBase}/api/profile/avatar`,
        { avatar_id: selectedAvatarId },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      toast.success("Avatar Updates!", {
        style: {
          border: "2px solid gold",
          padding: "12px",
          color: "#fff",
          background: "#111827",
        },
        iconTheme: {
          primary: "#FFD700",
          secondary: "#111827",
        },
      });
    } catch (err) {
      console.error(err);
      alert("Failed to update avatar");
    }
  }

  async function handleSaveFavoriteTeam() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) return alert("Not logged in");
      if (!selectedTeamId) return alert("Please select a team");

      const { data } = await axios.patch(
        `${apiBase}/api/profile/favorite-team`,
        { favorite_team_id: selectedTeamId },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      // update profile with the new team
      setProfile((prev) => ({
        ...prev,
        favorite_team: data.favorite_team,
        favorite_team_id: selectedTeamId,
      }));

      toast.success("Favorite team updated!", {
        style: {
          border: "2px solid gold",
          padding: "12px",
          color: "#fff",
          background: "#111827",
        },
        iconTheme: {
          primary: "#FFD700",
          secondary: "#111827",
        },
      });
    } catch (err) {
      console.error(err);
      alert("Failed to update favorite team");
    }
  }

  async function handleSaveUsername() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) return alert("Not logged in");
      if (!newDisplayName.trim()) return alert("Please enter a username");

      const { data } = await axios.patch(
        `${apiBase}/api/profile/username`,
        { display_name: newDisplayName.trim() },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      setProfile((prev) => ({ ...prev, display_name: data.profile.display_name }));
      setEditingName(false);
      toast.success("Username updated!", {
        style: {
          border: "2px solid gold",
          padding: "12px",
          color: "#fff",
          background: "#111827",
        },
        iconTheme: {
          primary: "#FFD700",
          secondary: "#111827",
        },
      });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to update username");
    }
  }

  if (loading) return <Loader loadingTitle={"Loading profile"} showLogo={true} />;
  if (error) return <p>Error: {error}</p>;
  if (!profile) return <p>No profile data.</p>;

  return (
    <div
      className="my-profile"
      style={{
        maxWidth: "1920px",
        margin: "auto",
        boxSizing: "border-box",
        display: "flex",
        minHeight: "100vh",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "10px",
      }}>
      <div style={{ width: "100%" }}>
        <Topbar profile={profile} />
        <div className="my-profile-container">
          <h2 style={{ color: "white", width: "100%" }}>My Profile</h2>
          <div className="profile-details">
            <div>
              <img
                src={`/avatars/${selectedAvatarPath}`}
                alt={profile.display_name}
                className="profile-avatar"
                style={{ cursor: "pointer" }} // show pointer to indicate click
                onClick={() => setShowAvatarPicker((prev) => !prev)} // toggle picker
              />
              <p style={{ margin: "0", color: "white", opacity: "0.6", fontSize: "11px" }}>Click to edit profile avatar</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "6px" }}>
              {editingName ? (
                <div style={{ display: "flex", gap: "8px", alignItems: "start", flexDirection: "column" }}>
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="Enter new username"
                    style={{
                      padding: "6px 8px",
                      borderRadius: "4px",
                      border: "1px solid #555",
                      background: "#111827",
                      color: "white",
                    }}
                  />
                  <div style={{ display: "flex", gap: "20px" }}>
                    <button
                      onClick={handleSaveUsername}
                      style={{
                        background: "#3273FA",
                        border: "none",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        color: "white",
                        cursor: "pointer",
                      }}>
                      Save
                    </button>
                    <button
                      onClick={() => setEditingName(false)}
                      style={{
                        background: "transparent",
                        color: "gray",
                        border: "none",
                        cursor: "pointer",
                      }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <h2
                  style={{ margin: "0", color: "white", cursor: "pointer" }}
                  onClick={() => {
                    setEditingName(true);
                    setNewDisplayName(profile.display_name || "");
                  }}>
                  {profile.display_name} ✎
                </h2>
              )}

              <XPBar xp={profile.xp || 0} />
            </div>
          </div>
          {showAvatarPicker && (
            <div className="badge-selection" style={{ marginTop: "20px" }}>
              <AvatarPicker
                userId={profile.id}
                selectedAvatar={selectedAvatarPath}
                onSelect={(path, id) => {
                  setSelectedAvatarPath(path);
                  setSelectedAvatarId(id);
                }}
              />
              <button
                style={{
                  marginTop: "1rem",
                  padding: "0.5rem 1rem",
                  borderRadius: "4px",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  background: "#3273FA",
                  opacity: !selectedAvatarId || selectedAvatarId === profile.avatar_id ? "0.4" : "1",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={handleSaveAvatar}
                disabled={!selectedAvatarId || selectedAvatarId === profile.avatar_id}>
                Save Avatar
              </button>
            </div>
          )}
          <h3 style={{ color: "white", width: "100%" }}>Badge Selection</h3>
          <div className="badge-selection">
            {profile.earned_badges.length >= 1 ? (
              <>
                <p style={{ margin: "0", color: "white", opacity: "0.6", fontSize: "11px" }}>
                  Selected {selectedBadges.length}/{MAX_DISPLAY_BADGES}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px",flexWrap: "wrap" }}>
                  {profile.earned_badges.map((badge) => (
                    <div
                      key={badge.id}
                      className={`badge ${selectedBadges.includes(badge.badge_name) ? "selected" : ""}`}
                      onClick={() => toggleBadge(badge.badge_name)}
                      style={{ display: "inline-block", cursor: "pointer", width: "auto" }}>
                      <img
                        src={`/badges/${badge.badge_image}`}
                        alt={badge.badge_name}
                        width={40}
                        style={{
                          border: selectedBadges.includes(badge.badge_name) ? "1px solid #3273fa" : "1px solid transparent",
                          borderRadius: "4px",
                          padding: "10px",
                          backgroundColor: "#3271fa10",
                          height: "42px",
                          width: "42px",
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  ))}
                </div>
                <button onClick={saveBadges} style={{ marginTop: "16px" }}>
                  Save Selection
                </button>
              </>
            ) : (
              <p style={{ color: "white", fontSize: "14px" }}>No badges earned</p>
            )}
          </div>
          <h3 style={{ color: "white", width: "100%" }}>Favorite Team</h3>
          {/* Favorite Team Selection */}
          <div style={{ width: "100%" }} className="badge-selection">
            {profile.favorite_team && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <img src={`/teams/${profile.favorite_team.team_logo}`} alt={profile.favorite_team.name} style={{ width: "28px" }} />
                <h4 style={{ color: "white" }}>{profile.favorite_team.name}</h4>
              </div>
            )}
            <select
              value={selectedTeamId || ""}
              onChange={(e) => setSelectedTeamId(e.target.value)} // always a string
              style={{ padding: "8px", marginTop: "10px" }}>
              <option value="" disabled>
                -- Select a team --
              </option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <button
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                marginLeft: "10px",
                background: "#3273FA",
                opacity: !selectedTeamId || selectedTeamId === profile.favorite_team_id ? "0.4" : "1",
                border: "none",
                cursor: "pointer",
              }}
              onClick={handleSaveFavoriteTeam}
              disabled={
                !selectedTeamId || selectedTeamId === profile.favorite_team_id // disable if no change
              }>
              Save Team
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
